import React, { useEffect, useMemo, useRef, useState } from "react";
import {Link} from "react-router-dom";
import makeCloudinaryUrlForPhoto from "../../react/makeCloudinaryUrlForPhoto";
import "./ProductSlideshow.scss";

const cn = (...xs) => xs.filter(Boolean).join(" ");

// Fisher–Yates shuffle (non-mutating)
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * ProductSlideshow
 * Props:
 * - categories: Array of categories with products
 * - products: (optional) direct products array; if provided, categories is ignored
 * - className: optional
 * - ratio: CSS aspect-ratio string (default "16 / 9"), e.g. "1 / 1" for square
 * - imgWidth: number for Cloudinary transform (default 1200)
 */
export default function ProductSlideshow({
  categories = [],
  products,
  className,
  ratio = "16 / 9",
  imgWidth = 1700,
}) {
  // Build the product list (with photos) and randomize
  const items = useMemo(() => {
    const fromCats = Array.isArray(categories)
      ? categories.flatMap((c) =>
          Array.isArray(c.products)
            ? c.products.map((p) => ({
                ...p,
                __category: c, // keep reference for title if you want
              }))
            : []
        )
      : [];

    const base = Array.isArray(products) ? products : fromCats;

    const withPhotos = base.filter((p) => !!p?.photo);
    return shuffle(withPhotos);
  }, [categories, products]);

  const [idx, setIdx] = useState(0);
  const [displayedUrl, setDisplayedUrl] = useState(null);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const requestIdRef = useRef(0);
  const touchStartX = useRef(null);

  const count = items.length;

  const buildUrl = (photo) =>
    makeCloudinaryUrlForPhoto(photo, { width: imgWidth, auto: "enhance" });

  // Initialize first image
  useEffect(() => {
    if (!count) {
      setDisplayedUrl(null);
      return;
    }
    const first = items[0];
    const url = buildUrl(first.photo);
    const img = new Image();
    img.src = url;
    img.onload = () => setDisplayedUrl(url);
    img.onerror = () => setDisplayedUrl(null);
  }, [count, items]); // rerun if items change (e.g., categories update)

  // Preload when idx changes; keep current until next is ready
  useEffect(() => {
    if (!count) return;

    const current = items[idx];
    const rid = ++requestIdRef.current;

    if (current?.photo) {
      setIsLoadingNext(true);
      const url = buildUrl(current.photo);
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (requestIdRef.current === rid) {
          setDisplayedUrl(url);
          setIsLoadingNext(false);
        }
      };
      img.onerror = () => {
        if (requestIdRef.current === rid) {
          setIsLoadingNext(false);
          // keep previous displayedUrl on error (no flash)
        }
      };
    } else {
      setIsLoadingNext(false);
      setDisplayedUrl(null);
    }
  }, [idx, items, count]);

  const go = (dir) => {
    if (!count) return;
    setIdx((i) => (i + dir + count) % count);
  };

  // Swipe gestures
  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    const threshold = 40;
    if (Math.abs(delta) > threshold) {
      if (delta < 0) go(+1);
      else go(-1);
    }
    touchStartX.current = null;
  };

  const current = items[idx];
  const productName = current?.name?.en || current?.name || "";
  const categoryTitle =
    current?.__category?.name?.en || current?.__category?.name || "";
  const productId = current?.id; // used for item detail link

  return (
    <div className={cn("product-slideshow", className)}>
      <div
        className="product-slideshow__media"
        style={{ "--ratio": ratio }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {displayedUrl ? (
          <img
            key={displayedUrl}
            src={displayedUrl}
            alt={productName || "Product image"}
            loading="lazy"
            className={cn("product-slideshow__img", isLoadingNext && "is-loading-next")}
          />
        ) : (
          <div className="product-slideshow__fallback">
            <span>No image</span>
          </div>
        )}

        {/* Spinner while next image preloads */}
        {isLoadingNext && (
          <div className="product-slideshow__spinner" aria-hidden>
            <div className="spinner-dot" />
          </div>
        )}

        {/* Overlay */}
        <div className="product-slideshow__overlay" />

        {/* Text + frosted container + item detail link */}
        <div className="product-slideshow__text">
          <div className="product-slideshow__info">
            {productName ? (
              <h3 className="product-slideshow__title">{productName}</h3>
            ) : null}
            {categoryTitle ? (
              <p className="product-slideshow__subtitle">{categoryTitle}</p>
            ) : null}
            {productId ? (
              <Link
                to={`/valley-village/products/${productId}?backURL=/order`}
                // state={{ backURL: '/valley-village/order'}}
                className="product-slideshow__link"
              >
                add to bag
              </Link>
            ) : null}
          </div>
        </div>

        {/* Nav controls */}
        {count > 1 && (
          <>
            <button
              type="button"
              className="product-slideshow__nav product-slideshow__nav--prev"
              aria-label="Previous"
              onClick={() => go(-1)}
            >
              <span aria-hidden>‹</span>
            </button>
            <button
              type="button"
              className="product-slideshow__nav product-slideshow__nav--next"
              aria-label="Next"
              onClick={() => go(+1)}
            >
              <span aria-hidden>›</span>
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {count > 1 && (
        <div className="product-slideshow__dots" role="tablist" aria-label="Slides pagination">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              className={cn("product-slideshow__dot", i === idx && "is-active")}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}